import { useCallback, useState } from "react";
import { toast } from "react-toastify";
import { useMedicalCareContext } from "@/contexts/MedicalCareContext";
import { generateMedicalTextAndCopy } from "@/utils/medicalTextBuilder";
import {
  useFilteredConditions,
  useFilteredAllergies,
  useFilteredImmunizations,
  useFilteredSelectedInterventions,
} from "@/pages/medicalcare/components/RegisterArea/useFilteredMedicalData";
import { useFormatter } from "@/utils/useFormatter";

export interface UseLuxorImportDataOptions {
  onImportSuccess: (textoImportado: string) => void;
  onCopyToEditor?: () => void;
  /** Se true, usuário editou a região importada desde o último import */
  isImportedDirty: boolean;
  /** Abre diálogo; retorna Promise<'keep' | 'replace'> */
  confirmReimport?: () => Promise<"keep" | "replace">;
}

export function useLuxorImportData({
  onImportSuccess,
  onCopyToEditor,
  isImportedDirty,
  confirmReimport,
}: UseLuxorImportDataOptions) {
  const { patient, problems, condutas, handleSubmit } = useMedicalCareContext();
  const { formatGender, formatAge } = useFormatter;

  const filteredConditions = useFilteredConditions();
  const filteredAllergies = useFilteredAllergies();
  const filteredImmunizations = useFilteredImmunizations();
  const textoInterventions = useFilteredSelectedInterventions();

  const [imported, setImported] = useState(false);

  const obterInformacoesPaciente = useCallback(() => {
    const educacaoExtension = patient?.extension?.find(
      (el: any) =>
        el.url === "http://terminology.hl7.org/ValueSet/v3-EducationLevel"
    );

    const escolaridade =
      educacaoExtension?.valueCodeableConcept?.coding?.[0]?.display || "-";

    return {
      idade: patient?.birthDate
        ? `${formatAge(`${patient?.birthDate}`)} anos`
        : "-",
      sexo: formatGender(patient?.gender) || "-",
      escolaridade,
    };
  }, [patient, formatAge, formatGender]);

  const applyImport = useCallback(
    (textoFinal: string) => {
      onImportSuccess(textoFinal);
      onCopyToEditor?.();
      setImported(true);
      setTimeout(() => setImported(false), 2000);
    },
    [onImportSuccess, onCopyToEditor]
  );

  const handleImport = useCallback(async () => {
    if (!handleSubmit) {
      onCopyToEditor?.();
      return;
    }

    const onSubmitLatest = async (data: any) => {
      const dataComContexto = {
        ...data,
        New_Conduta: condutas,
        problems,
      };

      if (isImportedDirty && confirmReimport) {
        const decision = await confirmReimport();
        if (decision === "keep") {
          return;
        }
      }

      generateMedicalTextAndCopy({
        data: dataComContexto,
        infosPaciente: obterInformacoesPaciente(),
        filteredConditions,
        filteredAllergies,
        filteredImmunizations,
        textoInterventions,
        setContent: applyImport,
        onSuccess: () =>
          toast.success("Dados do atendimento importados para o prompt"),
        onError: () => toast.error("Erro ao importar dados do atendimento"),
      });
    };

    handleSubmit(onSubmitLatest)();
  }, [
    handleSubmit,
    condutas,
    problems,
    obterInformacoesPaciente,
    filteredConditions,
    filteredAllergies,
    filteredImmunizations,
    textoInterventions,
    isImportedDirty,
    confirmReimport,
    applyImport,
    onCopyToEditor,
  ]);

  return {
    handleImport,
    imported,
  };
}
